import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Send, MapPin } from "lucide-react";

const EMAIL = "carburantepuc@gmail.com";
const PHONE = "8180820024";
const WHATSAPP_URL = `https://wa.me/91${PHONE}?text=Hi%20VayuCheck%2C%20I%20need%20help%20with%20`;

const SUBJECTS = ["General enquiry", "Booking help", "Partner onboarding", "Fleet / Commercial account", "Feedback", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) { toast.error("Please write at least 10 characters"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/contact", form);
      toast.success("Message sent — we usually reply within 24 hours.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="relative overflow-hidden bg-mesh-soft border-b border-border">
        <div className="absolute top-24 -left-16 w-72 h-72 rounded-full bg-primary/15 blur-3xl animate-drift" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-6 py-16 md:py-20">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1.5 rounded-full mb-6">
              <MessageCircle className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">Get in touch</span>
            </div>
            <h1 data-testid="contact-h1" className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-[1]">
              Talk to <span className="bg-gradient-to-r from-primary via-cyan-500 to-emerald-500 bg-clip-text text-transparent gradient-animated">VayuCheck</span>.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">
              Have a question about your PUC booking, want to list your centre, or need help managing a fleet? Reach out on any channel below — we usually reply within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-5 gap-10">
          {/* Contact channels */}
          <div className="md:col-span-2 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">Channels</div>

            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="block bg-white border border-border rounded-2xl p-5 hover:border-primary hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-primary" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Email</div>
                  <div className="font-mono font-semibold group-hover:text-primary transition-colors duration-200">{EMAIL}</div>
                </div>
              </div>
            </a>

            <a href={`tel:+91${PHONE}`} data-testid="contact-phone-link" className="block bg-white border border-border rounded-2xl p-5 hover:border-primary hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-primary" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Call us</div>
                  <div className="font-mono font-semibold group-hover:text-primary transition-colors duration-200">+91 {PHONE}</div>
                  <div className="text-xs text-muted-foreground mt-1">Mon–Sat, 9 AM–7 PM IST</div>
                </div>
              </div>
            </a>

            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="block bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-5 hover:border-emerald-400 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0"><MessageCircle className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-emerald-700 mb-1">WhatsApp</div>
                  <div className="font-mono font-semibold text-foreground group-hover:text-emerald-600 transition-colors duration-200">+91 {PHONE}</div>
                  <div className="text-xs text-muted-foreground mt-1">Fastest reply · start a chat</div>
                </div>
              </div>
            </a>

            <div className="bg-white border border-border rounded-2xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-muted-foreground" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Office</div>
                  <div className="font-semibold">VayuCheck HQ</div>
                  <div className="text-xs text-muted-foreground mt-1">India · registered in Bengaluru</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">Send us a message</div>
            <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-6 md:p-8 space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label>
                  <Input data-testid="contact-name" required value={form.name} onChange={setField("name")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone (optional)</Label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={setField("phone")} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="+91 …" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input data-testid="contact-email" type="email" required value={form.email} onChange={setField("email")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Subject</Label>
                <select
                  data-testid="contact-subject"
                  required
                  value={form.subject}
                  onChange={setField("subject")}
                  className="w-full rounded-lg bg-secondary border border-border h-11 mt-2 px-3 text-sm"
                >
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message</Label>
                <Textarea data-testid="contact-message" required minLength={10} value={form.message} onChange={setField("message")} className="rounded-lg bg-secondary border-border mt-2 min-h-[140px]" placeholder="Tell us how we can help…" />
              </div>
              <Button data-testid="contact-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 shadow-lg shadow-primary/25">
                {submitting ? "Sending…" : (<><Send className="w-4 h-4 mr-2" /> Send message</>)}
              </Button>
              {sent && <p data-testid="contact-thanks" className="text-sm text-emerald-700 text-center">Thanks! Your message reached our team.</p>}
              <p className="text-xs text-muted-foreground text-center">By submitting, you agree to our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</p>
            </form>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10 border-t border-border flex flex-wrap justify-between gap-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} VayuCheck</div>
        <div className="flex gap-6 flex-wrap">
          <Link to="/about" className="hover:text-foreground">About</Link>
          <Link to="/faqs" className="hover:text-foreground">FAQs</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
